import React, { useEffect, useState } from "react";
import styles from "./EmailTemplateModal.module.scss";
import toast from "react-hot-toast";
import InvitationDropdown from "./InvitationDropdown";
import { RichTextEditor } from "./RichTextEditor";

import {
    createEmail,
    updateEmail,
    fetchEmailById,
} from "@/services/api/EmailTemplate";

type Props = {
    onClose: () => void;
    editId?: string | number; // adjusted to handle both
    refreshList?: () => void;
};

const EmailTemplateModal: React.FC<Props> = ({
    onClose,
    editId,
    refreshList,
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const [form, setForm] = useState({
        name: "",
        emailSubject: "",
        replyToUserId: 0,   // stores the invitation ID (replyToUserDto.userId)
        body: "",
    });
    // Holds the email string for pre-populating the dropdown label on edit
    const [replyToEmail, setReplyToEmail] = useState<string>("");

    useEffect(() => {
        if (editId) {
            getEmailById();
        }
    }, [editId]);

    const getEmailById = async () => {
        try {
            setFetching(true);
            const res = await fetchEmailById(editId!.toString());
            const data = res?.data || res;

            if (data) {
                // API returns replyToUserDto: { userId, name, email }
                const replyDto = data.replyToUserDto || null;
                const replyId  = replyDto?.userId || data.replyToUserId || 0;
                const replyMail = replyDto?.email || "";

                setForm({
                    name: data.name || "",
                    emailSubject: data.emailSubject || "",
                    replyToUserId: replyId,
                    body: data.body || "",
                });
                setReplyToEmail(replyMail);
            }
        } catch (err) {
            toast.error("Failed to fetch email template");
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleReplyToChange = (id: number, _email: string) => {
        setForm((prev) => ({ ...prev, replyToUserId: id }));
    };

    const handleSubmit = async () => {
        if (!form.name.trim() || !form.emailSubject.trim()) {
            return toast.error("Template Name & Subject are required");
        }

        setLoading(true);
        try {
            const payload = {
                ...form,
                // send 0 as null-equivalent so backend knows "no reply-to"
                replyToUserId: form.replyToUserId || null,
                isDefault: false,
            };

            if (editId) {
                await updateEmail(payload, editId.toString());
                toast.success("Template updated successfully");
            } else {
                await createEmail(payload);
                toast.success("Template created successfully");
            }

            refreshList?.();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Failed to save template");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className={styles.overlay}>
                <div className={styles.modal} style={{ padding: '40px', textAlign: 'center' }}>
                    <p style={{ color: '#64748b' }}>Fetching template details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={styles.modal}>
                {/* HEADER */}
                <div className={styles.header}>
                    <h3>{editId ? "Edit Email Template" : "New Email Template"}</h3>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                {/* BODY */}
                <div className={styles.body}>
                    <div className={styles.inputGroup}>
                        <label>Template Name</label>
                        <input
                            className={styles.input}
                            name="name"
                            placeholder="e.g. Campus Recruitment Invite"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label>Email Subject</label>
                        <input
                            className={styles.input}
                            name="emailSubject"
                            placeholder="e.g. Invitation: Fullstack Developer Role at NCode"
                            value={form.emailSubject}
                            onChange={handleChange}
                        />
                    </div>

                    {/* Reply-to Address — infinite scroll dropdown from invitations API */}
                    <div className={styles.inputGroup}>
                        <label>Reply-to Address</label>
                        <InvitationDropdown
                            value={form.replyToUserId}
                            onChange={handleReplyToChange}
                            initialLabel={replyToEmail}
                        />
                        <span className={styles.fieldHint}>
                            Selected invitation's email will be used as the reply-to address.
                        </span>
                    </div>

                    <div className={styles.inputGroup}>
                        <label>Email Content</label>
                        <RichTextEditor
                            value={form.body}
                            onChange={(val) => setForm((prev) => ({ ...prev, body: val }))}
                            placeholder="Dear {{candidate_name}}, we would like to invite you to..."
                        />
                    </div>
                </div>

                {/* FOOTER */}
                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button className={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
                        {loading ? "Processing..." : editId ? "Update Template" : "Create Template"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailTemplateModal;